/**
 * Submit loading: disables the submit button and shows a spinner when a form
 * is submitted. Use on any form by adding data-submit-loading to the form.
 *
 * For the button: ensure it contains .btn-text (hidden when loading) and
 * .spinner-border (shown when loading). If .spinner-border is missing, one
 * is created automatically.
 */

const SPINNER_HTML =
  '<span class="spinner-border spinner-border-sm ml-2 d-none" role="status" aria-hidden="true"></span>';

function isButtonOrInput(
  el: HTMLElement
): el is HTMLButtonElement | HTMLInputElement {
  return el instanceof HTMLButtonElement || el instanceof HTMLInputElement;
}

export function showButtonLoading(button: HTMLElement): void {
  if (isButtonOrInput(button) && button.disabled) return;
  if (isButtonOrInput(button)) {
    button.disabled = true;
  } else {
    button.style.pointerEvents = "none";
    button.setAttribute("aria-disabled", "true");
    button.classList.add("disabled");
  }

  let textEl = button.querySelector(".btn-text");
  if (!textEl && button.textContent?.trim()) {
    textEl = document.createElement("span");
    textEl.className = "btn-text";
    textEl.textContent = button.textContent;
    button.textContent = "";
    button.appendChild(textEl);
  }
  if (textEl) textEl.classList.add("d-none");

  let spinner = button.querySelector(".spinner-border");
  if (!spinner) {
    button.insertAdjacentHTML("beforeend", SPINNER_HTML);
    spinner = button.querySelector(".spinner-border");
  }
  if (spinner) spinner.classList.remove("d-none");
}

export function resetButtonLoading(button: HTMLElement): void {
  if (isButtonOrInput(button)) {
    button.disabled = false;
  } else {
    button.style.pointerEvents = "";
    button.removeAttribute("aria-disabled");
    button.classList.remove("disabled");
  }
  const textEl = button.querySelector(".btn-text");
  if (textEl) textEl.classList.remove("d-none");
  const spinner = button.querySelector(".spinner-border");
  if (spinner) spinner.classList.add("d-none");
}

function initForm(form: HTMLFormElement): void {
  form.addEventListener("submit", function () {
    const submitBtn =
      form.querySelector<HTMLButtonElement | HTMLInputElement | HTMLElement>(
        "button[type=submit], input[type=submit], label.file-upload-button"
      );
    if (submitBtn) showButtonLoading(submitBtn);
  });
}

function resetAllSubmitLoading(): void {
  document
    .querySelectorAll<HTMLFormElement>("form[data-submit-loading]")
    .forEach((form) => {
      const submitBtn = form.querySelector<HTMLButtonElement | HTMLInputElement | HTMLElement>(
        "button[type=submit], input[type=submit], label.file-upload-button"
      );
      if (submitBtn) resetButtonLoading(submitBtn);
    });
}

function init(): void {
  document
    .querySelectorAll<HTMLFormElement>("form[data-submit-loading]")
    .forEach(initForm);
}

document.addEventListener("DOMContentLoaded", init);

// When user navigates back, the page may be restored from bfcache with the
// spinner still visible; reset all submit buttons so the UI is correct.
window.addEventListener("pageshow", (event) => {
  if (event.persisted) resetAllSubmitLoading();
});
